import { type Button, type ButtonProps, styled } from "@mui/material"
import type { ReactNode } from "react"
import { Check } from "react-feather"
import { FilterFieldButton } from "../FilterField"

export interface FilterToggleButtonProps
  extends Omit<
    ButtonProps<"label">,
    "onChange" | "variant" | "role" | "tabIndex" | "name"
  > {
  label: ReactNode
  checked?: boolean
  onChange: (checked: boolean) => void
  name?: string
}

export const FilterToggleButton = ({
  label,
  checked = false,
  onChange,
  name,
  ...buttonProps
}: FilterToggleButtonProps) => (
  // biome-ignore lint/a11y/useValidAriaRole: clears the role injected by ButtonBase
  <Label
    {...buttonProps}
    component="label"
    tabIndex={-1}
    role={undefined}
    variant={checked ? "contained" : "outlined"}
  >
    {checked && <Check size={20} />}
    {label}
    <Checkbox
      type="checkbox"
      name={name}
      checked={checked}
      // `disabled` on the label only cuts pointer events: the input stays
      // operable with the keyboard unless it is disabled too
      disabled={buttonProps.disabled}
      onChange={(event) => onChange(event.target.checked)}
    />
  </Label>
)

const Label = styled(FilterFieldButton)(({ theme: { spacing } }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: spacing(0.5),
  width: "fit-content",
})) as typeof Button

/** Same technique as MUI's `visuallyHidden`, unavailable from this package */
const Checkbox = styled("input")({
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  border: 0,
  overflow: "hidden",
  whiteSpace: "nowrap",
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
})
